/**
 * Onboarding Step 1 — Business profile.
 *
 * Revisit-aware: if a client already exists (user came back here from a
 * later step via Back), fetch the existing business profile via GET
 * /clients/{id}/business-profile and pass as initialValues. Submit
 * branches POST (initial create) vs PATCH (revisit) based on whether
 * memberships exist.
 *
 * RequireStep handles the "next_step === null → bounce to /" lockout, so
 * we never render here for fully-finished users.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/useAuth';
import { computeRailState } from '@/lib/onboardingState';
import { OnboardingSkeleton } from '@/components/OnboardingSkeleton';
import { OnboardingShell } from '@/components/onboarding/OnboardingShell';
import {
  BusinessProfileForm,
  type BusinessProfileFormValues,
} from '@/components/forms/BusinessProfileForm';

interface BusinessProfileResponse {
  business_name: string;
  owner_phone: string;
  auto_reply_text: string;
  website_url: string;
  timezone: string;
  currency: string;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const clientId = me?.memberships?.[0]?.client_id ?? '';
  const isRevisit = !!clientId;

  // Fetch existing business profile when revisiting (memberships exist).
  const profileQuery = useQuery({
    queryKey: ['client', clientId, 'business-profile'],
    queryFn: () =>
      apiRequest<BusinessProfileResponse>(`/clients/${clientId}/business-profile`),
    enabled: isRevisit,
  });

  const handleSubmit = async (values: BusinessProfileFormValues) => {
    try {
      if (isRevisit && clientId) {
        // PATCH the existing client (user came back to edit).
        await apiRequest(`/clients/${clientId}/business-profile`, {
          method: 'PATCH',
          body: values,
        });
      } else {
        // POST to create — initial signup path.
        await apiRequest<{ client_id: string }>('/onboarding/business-profile', {
          method: 'POST',
          body: values,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      await queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (clientId) {
        await queryClient.invalidateQueries({
          queryKey: ['client', clientId, 'business-profile'],
        });
      }
      navigate('/');
    } catch (e) {
      if (e instanceof ApiError) throw new Error(e.detail || e.message);
      throw e;
    }
  };

  // Show skeleton while loading existing profile data (revisit case).
  if (isRevisit && profileQuery.isLoading) {
    return <OnboardingSkeleton current={1} />;
  }

  const profile = profileQuery.data;
  const initialValues =
    isRevisit && profile
      ? {
          owner_name: me?.display_name || '',
          business_name: profile.business_name || '',
          website_url: profile.website_url || '',
          timezone: profile.timezone || '',
          currency: profile.currency || '',
        }
      : { owner_name: me?.display_name || '' };

  return (
    <OnboardingShell
      current={1}
      title={isRevisit ? 'Edit your business' : 'Set up your business'}
      subtitle="A few details so we can match every WhatsApp contact to the ad that brought them."
      state={computeRailState(me ?? null)}
    >
      <BusinessProfileForm
        initialValues={initialValues}
        emailReadOnly={me?.email || ''}
        onSubmit={handleSubmit}
        submitLabel={isRevisit ? 'Save changes' : 'Continue'}
        submittingLabel={isRevisit ? 'saving…' : 'setting up…'}
        showLegalFooter={!isRevisit}
      />
    </OnboardingShell>
  );
}
